import { useLayoutEffect, useRef } from "react";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";

type PageToken = number | "gap";

const SIBLING_COUNT = 1;
const BOUNDARY_COUNT = 1;

function range(from: number, to: number): number[] {
  const result: number[] = [];
  for (let value = from; value <= to; value += 1) result.push(value);
  return result;
}

/**
 * Список страниц для отображения: первая и последняя страница видны всегда,
 * вокруг текущей — сосед с каждой стороны (страница перед активной и после
 * неё), остальное схлопывается в «…». Соседей не меньше `SIBLING_COUNT=1` —
 * это гарантирует, что со страницы всегда можно шагнуть на её реального
 * соседа кликом, а не только стрелкой «›»/«‹».
 */
function pageTokens(page: number, lastPage: number): PageToken[] {
  const startPages = range(1, Math.min(BOUNDARY_COUNT, lastPage));
  const endPages = range(
    Math.max(lastPage - BOUNDARY_COUNT + 1, BOUNDARY_COUNT + 1),
    lastPage,
  );

  const siblingsStart = Math.max(
    Math.min(
      page - SIBLING_COUNT,
      lastPage - BOUNDARY_COUNT - SIBLING_COUNT * 2 - 1,
    ),
    BOUNDARY_COUNT + 2,
  );
  const firstEndPage = endPages[0];
  const siblingsEnd = Math.min(
    Math.max(page + SIBLING_COUNT, BOUNDARY_COUNT + SIBLING_COUNT * 2 + 2),
    firstEndPage !== undefined ? firstEndPage - 2 : lastPage - 1,
  );

  return [
    ...startPages,
    ...(siblingsStart > BOUNDARY_COUNT + 2
      ? (["gap"] as const)
      : BOUNDARY_COUNT + 1 < lastPage - BOUNDARY_COUNT
        ? [BOUNDARY_COUNT + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < lastPage - BOUNDARY_COUNT - 1
      ? (["gap"] as const)
      : lastPage - BOUNDARY_COUNT > BOUNDARY_COUNT
        ? [lastPage - BOUNDARY_COUNT]
        : []),
    ...endPages,
  ];
}

/**
 * Помечает, с какой стороны ряд номеров обрезан (`data-overflow="start end"`),
 * чтобы CSS затенил этот край: полоса прокрутки скрыта, и без затенения на
 * телефоне не видно, что за краем есть ещё страницы.
 */
function markOverflow(list: HTMLElement) {
  const maxScroll = list.scrollWidth - list.clientWidth;
  const edges: string[] = [];
  if (list.scrollLeft > 1) edges.push("start");
  if (list.scrollLeft < maxScroll - 1) edges.push("end");
  if (edges.length > 0) list.dataset.overflow = edges.join(" ");
  else delete list.dataset.overflow;
}

/**
 * Прокручивает ряд так, чтобы активная страница стояла по центру. Меняем
 * только `scrollLeft` самого ряда: `scrollIntoView` дёрнул бы ещё и
 * вертикальную прокрутку страницы.
 */
function centerCurrentPage(list: HTMLElement) {
  const current = list.querySelector<HTMLElement>('[aria-current="page"]');
  if (!current || list.scrollWidth <= list.clientWidth) return;
  list.scrollLeft =
    current.offsetLeft - (list.clientWidth - current.offsetWidth) / 2;
}

export function Pagination({
  page,
  lastPage,
  total,
  perPage,
  label,
  busy = false,
  onChange,
}: {
  page: number;
  lastPage: number;
  total: number;
  perPage: number;
  label: string;
  /** Идёт загрузка страницы: кнопки на время заблокированы, чтобы не увести на две страницы разом. */
  busy?: boolean;
  onChange: (page: number) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    centerCurrentPage(list);
    markOverflow(list);
  }, [page, lastPage, total]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;
    // Ширина ряда меняется вместе с окном и боковым меню — пересчитываем края.
    const observer = new ResizeObserver(() => {
      centerCurrentPage(list);
      markOverflow(list);
    });
    observer.observe(list);
    return () => observer.disconnect();
  }, [lastPage, total]);

  if (total === 0) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <nav
      className="pagination pagination--numbered"
      aria-label={label}
      aria-busy={busy || undefined}
    >
      <span className="pagination__summary" aria-live="polite" aria-atomic="true">
        Показано {from}–{to} из {total}
      </span>
      {lastPage > 1 ? (
        // Стрелки стоят вне прокручиваемого ряда номеров: на узком экране
        // ряд прокручивается, а «назад/вперёд» всегда остаются под пальцем.
        <div className="pagination__bar">
          <button
            type="button"
            className="pagination__step"
            disabled={busy || page <= 1}
            onClick={() => onChange(page - 1)}
            aria-label="Предыдущая страница"
          >
            <HiOutlineChevronLeft aria-hidden="true" />
          </button>
          <ul
            ref={listRef}
            className="pagination__pages"
            onScroll={(event) => markOverflow(event.currentTarget)}
          >
            {pageTokens(page, lastPage).map((token, index) =>
              token === "gap" ? (
                <li
                  key={`gap-${index}`}
                  className="pagination__gap"
                  aria-hidden="true"
                >
                  …
                </li>
              ) : (
                <li key={token}>
                  <button
                    type="button"
                    className="pagination__page"
                    disabled={busy}
                    aria-current={token === page ? "page" : undefined}
                    onClick={() => onChange(token)}
                  >
                    {token}
                  </button>
                </li>
              ),
            )}
          </ul>
          <button
            type="button"
            className="pagination__step"
            disabled={busy || page >= lastPage}
            onClick={() => onChange(page + 1)}
            aria-label="Следующая страница"
          >
            <HiOutlineChevronRight aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </nav>
  );
}
